import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

interface DeviceContextProps {
  isTouch: boolean;
}

const DeviceContext = createContext<DeviceContextProps>({
  isTouch: false,
});

interface DeviceProviderProps {
  children: ReactNode;
}

const DeviceProvider: React.FC<DeviceProviderProps> = ({ children }) => {
  const [isTouchDevice, setIsTouchDevice] = useState<boolean>(isTouch());

  useEffect(() => {
    // Update on window resize/orientation change
    const handleChange = () => {
      setIsTouchDevice(isTouch());
    };

    window.addEventListener('resize', handleChange);
    window.addEventListener('orientationchange', handleChange);

    return () => {
      window.removeEventListener('resize', handleChange);
      window.removeEventListener('orientationchange', handleChange);
    };
  }, []);

  return (
    <DeviceContext.Provider
      value={{
        isTouch: isTouchDevice,
      }}
    >
      {children}
    </DeviceContext.Provider>
  );
};

const useDeviceContext = () => {
  return useContext(DeviceContext);
};

// Matches Tailwind's `lg` breakpoint — below this, a touch-capable device
// is treated as mobile. At or above it, e.g. a touch-enabled laptop, it isn't.
const MOBILE_MAX_WIDTH_PX = 1024;

const isMobileUserAgent = (): boolean =>
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  );

const isTouchCapable = (): boolean =>
  'ontouchstart' in window || navigator.maxTouchPoints > 0;

// Touch capability alone isn't enough to mean "mobile" — touch-enabled
// laptops report maxTouchPoints > 0 too, so also require a narrow viewport
// unless the user agent itself identifies a mobile/tablet OS.
const isTouch = (): boolean =>
  isMobileUserAgent() ||
  (isTouchCapable() && window.innerWidth < MOBILE_MAX_WIDTH_PX);

export { DeviceContext, DeviceProvider, useDeviceContext };
